import type { ReactElement } from 'react'
import { Info, Store } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { startBrowserTransition } from '../components/BrowserTransition.js'
import { activePersona } from '../lib/persona.js'
import { activeBrand } from '../lib/brand.js'

export const EmailScreen = (): ReactElement => {
  const p = activePersona()
  const brand = activeBrand()
  const onPrepare = (): void => {
    startBrowserTransition({
      targetStep: 'landing',
      targetUrl: 'veritact.com/readiness/start',
    })
  }
  return (
    <AppShell title="Email notification" showBack onBack={() => history.back()}>
      <div className={brand.emailClassName || undefined}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-white ring-1 ring-border flex items-center justify-center overflow-hidden">
            {brand.logoSrc ? (
              <img
                src={brand.logoSrc}
                alt={brand.senderName}
                className="h-5 w-auto object-contain"
              />
            ) : (
              <Store className="h-5 w-5 text-muted-foreground" aria-hidden />
            )}
          </div>
          <div>
            <div className="font-semibold">{brand.senderName}</div>
            <div className="text-xs opacity-70">to me</div>
          </div>
        </div>

        {brand.logoSrc && (
          <img
            src={brand.logoSrc}
            alt={brand.senderName}
            className="h-7 w-auto mb-4"
          />
        )}

        <div className="wm-banner border-l-4 rounded-r-xl px-4 py-3 font-semibold text-sm mb-5 inline-flex items-center gap-2">
          <Info className="h-4 w-4" />
          Action required for installation
        </div>

        <h1 className="mb-4">Let's get your kitchen ready for your dishwasher</h1>

        <div className="rounded-xl p-4 mb-5 flex items-center gap-4 bg-white border border-[color:var(--wm-blue)]/20">
          <img
            src="/demo/whirlpool-dishwasher.png"
            alt={p.order.appliance}
            className="h-24 w-auto shrink-0 object-contain"
          />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider wm-card-label font-semibold">Your order</div>
            <div className="font-bold mt-0.5">{p.order.appliance}</div>
            <div className="text-xs opacity-70 mt-1">Order #{p.order.number}</div>
          </div>
        </div>

        <div className="wm-card rounded-xl p-4 mb-5">
          <div className="text-xs uppercase tracking-wider wm-card-label font-semibold text-center">Order details</div>
          <div className="font-bold text-lg text-center mt-1">Order #{p.order.number}</div>
          <div className="text-sm opacity-80 text-center">Estimated installation: scheduled after readiness check</div>
        </div>

        <ul className="space-y-2 mb-6">
          <li>• Measure cabinet opening</li>
          <li>• Check water line connections</li>
          <li>• Verify electrical requirements</li>
        </ul>

        <Button size="xl" onClick={onPrepare} className="wm-cta w-full">
          Prepare My Kitchen
        </Button>
        <p className="text-sm text-center mt-2 opacity-80">
          Takes just a moment — and helps us deliver on time.
        </p>

        <p className="text-xs opacity-70 text-center mt-4">
          Why do I need to do this? Our installers need specific measurements to ensure your appliance can be safely and correctly installed.
        </p>
      </div>
    </AppShell>
  )
}
